import { pick, prop } from 'ramda';
import { useEffect } from 'react';
import { useInvokeAction } from 'server';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslations } from 'js/hooks/i18n';
import { useRemoveQueryParams } from 'js/hooks/navigate';
import { useGetErrorMessage } from 'js/hooks/server/hooks';
import { useAppSelector, usePageSelector } from 'js/store/hooks';
import { showCommonPopupAction } from 'js/components/common/popups/CommonPopup';
import { useRequestMultiFactorAuthentication } from 'js/components/popups/MultiFactorAuthenticationPopup';

export const useUnblockAccount = () => {
    const translate = useTranslations();
    const location = useLocation();
    const dispatch = useDispatch();
    const removeQueryParams = useRemoveQueryParams();
    const casinoName = useAppSelector(prop('casinoName'));
    const invokeAction = useInvokeAction();
    const requestMultiFactorAuthentication = useRequestMultiFactorAuthentication();
    const { isPageRendering } = usePageSelector(pick(['isPageRendering']));
    const getErrorMessage = useGetErrorMessage({ tkPrefix: 'playtech.mobile.system.accountUnblock.' });

    useEffect(() => {
        if (isPageRendering) {
            return;
        }

        const token = new URLSearchParams(location.search).get('2faToken');

        if (!token || !casinoName) {
            return;
        }

        (async () => {
            try {
                const { result } = await invokeAction({
                    casino: casinoName,
                    token
                });
                const {
                    provider,
                    actionToken
                } = Object.fromEntries(result?.map(({
                    key,
                    value
                }: {
                    key: string;
                    value: string;
                }) => [key, value]) ?? []);

                switch (provider) {
                    case 'caf':
                    case 'unico': {
                        try {
                            await requestMultiFactorAuthentication({
                                initiator: 'unblock-account',
                                onSuccess: () => {},
                                onCancel: () => {},
                                onCustomValidate: (json: string) => invokeAction({
                                    casino: casinoName,
                                    token: actionToken,
                                    parameters: [
                                        { key: 'mfaResult', value: json }
                                    ]
                                }),
                                provider,
                                properties: result
                            });
                        } catch {
                            // player has canceled the process
                            return;
                        }

                        break;
                    }

                    default:
                        // noop
                }

                dispatch(showCommonPopupAction({
                    title: 'playtech.mobile.system.accountUnblock.success.title',
                    content: 'playtech.mobile.system.accountUnblock.success.content'
                }));
            } catch (error: any) {
                dispatch(showCommonPopupAction({
                    content: getErrorMessage(error, translate('playtech.mobile.system.error.default'))
                }));
            } finally {
                removeQueryParams(['2faToken']);
            }
        })();
    }, [
        translate,
        isPageRendering,
        casinoName,
        dispatch,
        location.search,
        removeQueryParams,
        invokeAction,
        getErrorMessage,
        requestMultiFactorAuthentication
    ]);
};
